import { Edit01 } from '@untitledui/icons';
import { useCallback } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import { getFieldTypeLabel } from '@/lib/crm/utils/fieldTypeHelpers';
import {
  getEntityKindIconComponent,
  getIconComponentByName,
} from '@/lib/crm/utils/icons/EntityTypeIcon';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import type { AssociationDefinition } from '../InlineAssociationEditor';
import type { PropertyDefinition } from '../InlinePropertyEditor';
import {
  OBJECT_TYPE_WIZARD_STEPS,
  type ObjectTypeWizardStepId,
} from './objectTypeWizardSteps';
import type { StageItem } from './StageManager';

// =============================================================================
// ReviewSection — a collapsible card section in the review step
// =============================================================================

export function ReviewSection({
  title,
  count,
  onEdit,
  children,
  emptyMessage,
}: {
  title: string;
  count?: number;
  onEdit: () => void;
  children?: React.ReactNode;
  emptyMessage?: string;
}) {
  return (
    <div className='rounded-xl border border-border-secondary bg-bg-secondary overflow-hidden'>
      <div className='flex items-center justify-between px-4 py-3 border-b border-border-secondary'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-semibold text-primary'>{title}</span>
          {count !== undefined && count > 0 && (
            <Badge type='pill-color' size='sm' color='gray'>
              {count}
            </Badge>
          )}
        </div>
        <button
          type='button'
          onClick={onEdit}
          className='flex items-center gap-1.5 text-xs text-fg-brand-primary hover:text-utility-brand-700 font-medium transition-colors'
        >
          <Edit01 className='h-3.5 w-3.5' />
          Edit
        </button>
      </div>
      <div className='px-4 py-3'>
        {children ?? (
          <p className='text-sm text-tertiary'>
            {emptyMessage ?? 'None configured'}
          </p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ReviewStep — summary view of all wizard configuration
// =============================================================================

export function ReviewStep({
  displayName,
  pluralName,
  icon,
  ontologyKindLabel,
  properties,
  associations,
  notesEnabled,
  isPipelineable,
  stages,
  onStepClick,
}: {
  displayName: string;
  pluralName: string;
  icon: string | null;
  /** Plain-language name for the chosen kind, as shown on the Type step. */
  ontologyKindLabel: string;
  properties: PropertyDefinition[];
  associations: AssociationDefinition[];
  notesEnabled: boolean;
  isPipelineable: boolean;
  stages: StageItem[];
  /**
   * Called when the user clicks an "Edit" button on a review section, with the
   * target step's id.
   *
   * Ids, not indices: the wizard's step list is conditional (the Pipeline step
   * only exists for one kind), so a positional target silently points at the
   * wrong step – or off the end – as soon as the list changes shape.
   */
  onStepClick: (stepId: ObjectTypeWizardStepId) => void;
}) {
  const { data: fieldTypes = [] } = useFieldTypes();
  // The wizard always includes entity_kind 'default' (buildObjectTypeEvents.ts)
  // alongside the chosen ontology kind, so the no-icon preview shows the
  // 'default' kind icon — matching what the nav and settings will render.
  const IconComponent = icon
    ? getIconComponentByName(icon)
    : getEntityKindIconComponent('default');

  const handleEditBasics = useCallback(
    () => onStepClick(OBJECT_TYPE_WIZARD_STEPS.basics),
    [onStepClick]
  );
  const handleEditKind = useCallback(
    () => onStepClick(OBJECT_TYPE_WIZARD_STEPS.kind),
    [onStepClick]
  );
  const handleEditFields = useCallback(
    () => onStepClick(OBJECT_TYPE_WIZARD_STEPS.fields),
    [onStepClick]
  );
  const handleEditAssociations = useCallback(
    () => onStepClick(OBJECT_TYPE_WIZARD_STEPS.associations),
    [onStepClick]
  );
  const handleEditPipeline = useCallback(
    () => onStepClick(OBJECT_TYPE_WIZARD_STEPS.pipeline),
    [onStepClick]
  );

  return (
    <div className='space-y-4'>
      {/* Object Info */}
      <ReviewSection title='Object info' onEdit={handleEditBasics}>
        <div className='flex items-center gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-bg-tertiary shrink-0'>
            <IconComponent className='h-5 w-5 text-secondary' />
          </div>
          <div>
            <p className='text-sm font-medium text-primary'>{displayName}</p>
            <p className='text-xs text-tertiary'>Plural: {pluralName}</p>
          </div>
        </div>
      </ReviewSection>

      {/* Type */}
      <ReviewSection title='Type' onEdit={handleEditKind}>
        <p className='text-sm text-primary'>{ontologyKindLabel}</p>
      </ReviewSection>

      {/* Fields */}
      <ReviewSection
        title='Fields'
        count={properties.length}
        onEdit={handleEditFields}
        emptyMessage='No custom fields configured'
      >
        {properties.length > 0 ? (
          <div className='space-y-1'>
            {properties.map(prop => {
              const PropIcon = getPropertyTypeIconComponent({
                field_type_name: prop.fieldType,
              });
              const fieldLabel = getFieldTypeLabel(prop.fieldType, fieldTypes);
              return (
                <div key={prop.id} className='flex items-center gap-2 py-1'>
                  {PropIcon && (
                    <PropIcon className='h-4 w-4 text-tertiary shrink-0' />
                  )}
                  <span className='text-sm text-primary flex-1'>
                    {prop.label}
                  </span>
                  <Badge type='pill-color' size='sm' color='gray'>
                    {fieldLabel}
                  </Badge>
                </div>
              );
            })}
          </div>
        ) : null}
      </ReviewSection>

      {/* Associations */}
      <ReviewSection
        title='Associations'
        count={associations.length + (notesEnabled ? 1 : 0)}
        onEdit={handleEditAssociations}
        emptyMessage='No associations configured'
      >
        {associations.length > 0 || notesEnabled ? (
          <div className='space-y-1'>
            {notesEnabled && (
              <div className='flex items-center gap-2 py-1'>
                <span className='text-sm text-primary flex-1'>Notes</span>
                <Badge type='pill-color' size='sm' color='brand'>
                  Many-to-many
                </Badge>
              </div>
            )}
            {associations.map(assoc => {
              const cardinalityLabel: Record<string, string> = {
                one_to_one: 'One-to-one',
                one_to_many: 'One-to-many',
                many_to_one: 'Many-to-one',
                many_to_many: 'Many-to-many',
              };
              return (
                <div key={assoc.id} className='flex items-center gap-2 py-1'>
                  <span className='text-sm text-primary flex-1'>
                    {assoc.displayName || assoc.targetEntityTypeName}
                  </span>
                  <Badge type='pill-color' size='sm' color='gray'>
                    {cardinalityLabel[assoc.cardinality] ?? assoc.cardinality}
                  </Badge>
                </div>
              );
            })}
          </div>
        ) : null}
      </ReviewSection>

      {/*
        Stages — only for a kind that rides a pipeline. Shown conditionally
        because its "Edit" jumps to the Pipeline step, and that step isn't in
        the wizard at all for the other kinds.
      */}
      {isPipelineable && (
        <ReviewSection
          title='Stages'
          count={stages.length}
          onEdit={handleEditPipeline}
          emptyMessage='No stages configured'
        >
          {stages.length > 0 ? (
            <div className='space-y-1'>
              {stages.map((stage, i) => (
                <div key={stage.id} className='flex items-center gap-2 py-1'>
                  <span className='text-xs text-tertiary w-5 shrink-0'>
                    {i + 1}.
                  </span>
                  <span className='text-sm text-primary'>
                    {stage.name || (
                      <em className='text-tertiary'>Unnamed stage</em>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </ReviewSection>
      )}
    </div>
  );
}
